import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import FileUploadIcon from '@rsuite/icons/FileUpload'
import CloseIcon from '@rsuite/icons/Close'
import { Button, Modal } from 'shared'
import { uploadDocument, invalidateDocuments } from '../documentsApiSlice'
import { errorCode, errorMessage, formatBytes } from '../utils'
import CopyChip from './CopyChip'

type Props = {
  open: boolean
  clientId: string
  clientName?: string
  inboxEmail?: string
  onClose: () => void
  /** Called after at least one file was added, to show the Processing tab. */
  onViewProcessing: () => void
}

type FileState = 'queued' | 'uploading' | 'done' | 'duplicate' | 'failed'

type QueuedFile = {
  key: string
  file: File
  state: FileState
  progress: number
  message?: string
}

// Document AI only processes PDFs; the backend also checks the file signature.
const MAX_MB = 50
const MAX_BYTES = MAX_MB * 1024 * 1024
const CONCURRENCY = 3

const STATE_LABEL: Record<FileState, string> = {
  queued: 'Ready',
  uploading: 'Uploading…',
  done: 'Added — processing started',
  duplicate: 'Already added',
  failed: 'Failed'
}

const isPdf = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name)

const UploadDocumentsModal: React.FC<Props> = ({
  open,
  clientId,
  clientName,
  inboxEmail,
  onClose,
  onViewProcessing
}) => {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<QueuedFile[]>([])
  const [rejected, setRejected] = useState<string[]>([])
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)

  const update = (key: string, patch: Partial<QueuedFile>) =>
    setFiles((prev) =>
      prev.map((f) => (f.key === key ? { ...f, ...patch } : f))
    )

  const addFiles = (list: FileList | null) => {
    if (!list) return
    const accepted: QueuedFile[] = []
    const problems: string[] = []
    Array.from(list).forEach((file) => {
      if (!isPdf(file)) {
        problems.push(`${file.name} is not a PDF`)
      } else if (file.size > MAX_BYTES) {
        problems.push(`${file.name} is larger than ${MAX_MB} MB`)
      } else if (file.size === 0) {
        problems.push(`${file.name} is empty`)
      } else {
        accepted.push({
          key: `${file.name}-${file.size}-${file.lastModified}`,
          file,
          state: 'queued',
          progress: 0
        })
      }
    })
    setRejected(problems)
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => f.key))
      return [...prev, ...accepted.filter((f) => !seen.has(f.key))]
    })
  }

  const uploadOne = async (item: QueuedFile) => {
    update(item.key, { state: 'uploading', progress: 0, message: undefined })
    try {
      const response = await uploadDocument(clientId, item.file, (progress) =>
        update(item.key, { progress })
      )
      // The shared 403 interceptor resolves (with a redirect) instead of
      // rejecting, so only count it as added when the document came back.
      if (!response?.data?.id) {
        throw new Error('Upload was not accepted')
      }
      update(item.key, { state: 'done', progress: 1 })
    } catch (error) {
      const duplicate = errorCode(error) === 'document.duplicate'
      update(item.key, {
        state: duplicate ? 'duplicate' : 'failed',
        message: errorMessage(error, 'Upload failed. Please try again.')
      })
    }
  }

  const startUpload = async () => {
    setBusy(true)
    const pending = files.filter(
      (f) => f.state === 'queued' || f.state === 'failed'
    )
    let next = 0
    const worker = async () => {
      while (next < pending.length) {
        const item = pending[next]
        next += 1
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(item)
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker)
    )
    setBusy(false)
    dispatch(invalidateDocuments())
  }

  const close = () => {
    if (busy) return
    setFiles([])
    setRejected([])
    onClose()
  }

  const pendingCount = files.filter(
    (f) => f.state === 'queued' || f.state === 'failed'
  ).length
  const doneCount = files.filter((f) => f.state === 'done').length

  const body = (
    <div className="dx-upload">
      <button
        type="button"
        className={classNames('dx-drop', { 'dx-drop--active': dragging })}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          addFiles(e.dataTransfer.files)
        }}
        disabled={busy}
      >
        <span className="dx-drop__icon">
          <FileUploadIcon />
        </span>
        <span className="dx-drop__title">
          <strong>Click to select</strong> or drag PDFs here
        </span>
        <span className="dx-drop__hint">
          PDF only · up to {MAX_MB} MB each
          {clientName ? ` · added to ${clientName}` : ''}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </button>

      {rejected.length > 0 && (
        <ul className="dx-upload__rejected" role="alert">
          {rejected.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <ul className="dx-upload__list">
          {files.map((f) => (
            <li
              key={f.key}
              className={`dx-upload__item dx-upload__item--${f.state}`}
            >
              <div className="dx-upload__row">
                <span className="dx-upload__name dx-mono" title={f.file.name}>
                  {f.file.name}
                </span>
                <span className="dx-upload__size">
                  {formatBytes(f.file.size)}
                </span>
                <span className="dx-upload__state">{STATE_LABEL[f.state]}</span>
                {!busy && f.state !== 'done' && (
                  <button
                    type="button"
                    className="dx-icon-btn dx-icon-btn--sm"
                    aria-label={`Remove ${f.file.name}`}
                    onClick={() =>
                      setFiles((prev) => prev.filter((p) => p.key !== f.key))
                    }
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
              {(f.state === 'uploading' || f.state === 'done') && (
                <span className="dx-progress" aria-hidden>
                  <span style={{ width: `${Math.round(f.progress * 100)}%` }} />
                </span>
              )}
              {f.message && (
                <span className="dx-upload__message">{f.message}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {inboxEmail && (
        <p className="dx-upload__alt">
          You can also forward documents to{' '}
          <CopyChip value={inboxEmail} label="inbox address" />
        </p>
      )}
    </div>
  )

  const footer = (
    <div className="dx-modal-footer">
      <Button appearance="subtle" onClick={close} disabled={busy}>
        {doneCount > 0 && pendingCount === 0 ? 'Close' : 'Cancel'}
      </Button>
      {doneCount > 0 && pendingCount === 0 ? (
        <Button
          appearance="primary"
          className="dx-btn-primary"
          onClick={() => {
            close()
            onViewProcessing()
          }}
        >
          View processing
        </Button>
      ) : (
        <Button
          appearance="primary"
          className="dx-btn-primary"
          disabled={pendingCount === 0 || busy}
          loading={busy}
          onClick={startUpload}
        >
          {files.some((f) => f.state === 'failed')
            ? `Retry ${pendingCount} file${pendingCount === 1 ? '' : 's'}`
            : `Upload ${pendingCount || ''} file${pendingCount === 1 ? '' : 's'}`}
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={close}
      size="sm"
      title="Upload documents"
      body={body}
      footer={footer}
      dialogClassName="dx-modal"
    />
  )
}

export default UploadDocumentsModal
